/**
 * Custom-domain helpers shared by the `verify-custom-domain` and
 * `recheck-custom-domains` edge functions.
 *
 * Runtime-agnostic on purpose (no `Deno.*`, no remote imports) so the same logic
 * runs inside the Deno edge runtime AND under a Node/Playwright unit test —
 * mirroring `_shared/stripe.ts` and `_shared/payment-link.ts`. It relies only on
 * Web Platform globals present in both runtimes: `fetch` and `URLSearchParams`.
 *
 * The frontend mirror is `src/lib/customDomain.ts` (normalisation + DNS
 * instructions); tests/custom-domain-lib.spec.ts proves the two agree, and that
 * the multi-part suffix list matches the SQL `custom_domain_apex()` helper.
 *
 * Flow for one domain (see checkCustomDomain):
 *   1. Ownership — a DNS-over-HTTPS TXT lookup of `_polished-verify.<domain>`.
 *   2. Attach — add the domain (and `www.` for an apex domain, redirecting to the
 *      apex) to OUR Vercel project. "Already in use" only counts as success when
 *      the domain is genuinely on our project.
 *   3. Vercel verification — if Vercel needs its own `_vercel` TXT challenge
 *      (domain used by another Vercel account), we surface that record.
 *   4. DNS — Vercel's domain config endpoint says whether the A/CNAME records
 *      point at Vercel, and gives the project-specific recommended values.
 *   5. SSL — once DNS is right Vercel issues the certificate; a real HTTPS
 *      request to the domain tells us when the padlock is there.
 *
 * FAILS SAFE: with no Vercel credentials we report `unconfigured` — never a fake
 * `active`.
 */

/** Label we ask owners to publish the challenge token under. */
export const VERIFICATION_PREFIX = "_polished-verify";

/** Our own platform domain, served by the subdomain system — not a custom domain. */
export const PLATFORM_DOMAIN = "thepolishedversion.com";

/** Vercel's general-purpose values, used until Vercel tells us project-specific ones. */
export const DEFAULT_APEX_A = "76.76.21.21";
export const DEFAULT_CNAME = "cname.vercel-dns.com";

/**
 * Public suffixes with two labels that people commonly register under. Keeps
 * `acme.co.uk` recognised as an apex (needs an A record) rather than a
 * subdomain of `co.uk`. MUST match the array in the SQL `custom_domain_apex()`
 * function (asserted by tests/custom-domain-lib.spec.ts).
 */
export const MULTI_PART_SUFFIXES = [
  "co.uk", "org.uk", "me.uk", "ltd.uk", "plc.uk", "net.uk", "sch.uk", "ac.uk", "gov.uk", "nhs.uk",
  "uk.com", "uk.net", "gb.com", "eu.com",
  "com.au", "net.au", "org.au",
  "co.nz", "org.nz", "net.nz",
  "co.za", "co.in", "co.jp", "com.br", "com.mx", "com.sg", "com.hk",
] as const;

const SUFFIX_SET = new Set<string>(MULTI_PART_SUFFIXES);

/** Public DNS-over-HTTPS resolvers (JSON API). We try them in order. */
const DOH_ENDPOINTS = [
  "https://dns.google/resolve",
  "https://cloudflare-dns.com/dns-query",
];

const VERCEL_API_BASE = "https://api.vercel.com";

// ---------------------------------------------------------------------------
// Normalisation
// ---------------------------------------------------------------------------

/**
 * Trim, lower-case, strip scheme, credentials, path/query/hash, port and any
 * trailing dot — same rules as the SQL `custom_domain_normalise()`.
 */
export function normaliseDomain(raw: string): string {
  return String(raw ?? "")
    .trim()
    .toLowerCase()
    .replace(/^[a-z][a-z0-9+.-]*:\/\//, "")
    .replace(/[/?#].*$/, "")
    .replace(/^[^@]*@/, "")
    .replace(/:\d*$/, "")
    .replace(/\.+$/, "");
}

const HOST_PATTERN = /^([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z][a-z0-9-]{0,61}[a-z0-9]$/;

/** Is this a syntactically valid multi-label hostname? */
export function isValidHostname(host: string): boolean {
  return host.length <= 253 && HOST_PATTERN.test(host);
}

/** The registrable ("apex") domain for a host: `www.acme.co.uk` → `acme.co.uk`. */
export function apexOf(host: string): string {
  const labels = normaliseDomain(host).split(".");
  if (labels.length <= 2) return labels.join(".");
  const lastTwo = labels.slice(-2).join(".");
  return labels.slice(SUFFIX_SET.has(lastTwo) ? -3 : -2).join(".");
}

export function isApexDomain(host: string): boolean {
  const h = normaliseDomain(host);
  return apexOf(h) === h && !SUFFIX_SET.has(h);
}

/** The fully-qualified TXT record name the owner must create. */
export function verificationHost(domain: string): string {
  return `${VERIFICATION_PREFIX}.${normaliseDomain(domain)}`;
}

/** True for our own platform domain / its subdomains (handled elsewhere). */
export function isManagedSubdomain(domain: string): boolean {
  const d = normaliseDomain(domain);
  return d === PLATFORM_DOMAIN || d.endsWith(`.${PLATFORM_DOMAIN}`);
}

export type CanonicalDomain =
  | { ok: true; domain: string; includeWww: boolean; apex: string }
  | { ok: false; reason: "empty" | "invalid" | "platform" };

/**
 * What the owner typed → the domain we store. `www.acme.co.uk` and
 * `acme.co.uk` both become `acme.co.uk` with `includeWww` (we serve the apex and
 * redirect www → apex). Any other subdomain (`bookings.acme.co.uk`) is stored
 * as-is, without a www variant.
 */
export function canonicaliseCustomDomain(raw: string): CanonicalDomain {
  const n = normaliseDomain(raw);
  if (!n) return { ok: false, reason: "empty" };
  if (!isValidHostname(n) || SUFFIX_SET.has(n)) return { ok: false, reason: "invalid" };
  if (isManagedSubdomain(n)) return { ok: false, reason: "platform" };
  if (n.startsWith("www.") && isApexDomain(n.slice(4))) {
    const domain = n.slice(4);
    return { ok: true, domain, includeWww: true, apex: domain };
  }
  if (isApexDomain(n)) return { ok: true, domain: n, includeWww: true, apex: n };
  return { ok: true, domain: n, includeWww: false, apex: apexOf(n) };
}

/** Every hostname we attach for a stored domain — primary first. */
export function customDomainHosts(domain: string, includeWww: boolean): string[] {
  return includeWww ? [domain, `www.${domain}`] : [domain];
}

// ---------------------------------------------------------------------------
// DNS instructions
// ---------------------------------------------------------------------------

export interface VercelChallenge {
  type: string;
  domain: string;
  value: string;
  reason?: string;
}

export interface DnsRecord {
  key: string;
  type: "A" | "CNAME" | "TXT";
  /** What to type in the provider's Name/Host box (relative to the apex, `@` = apex). */
  name: string;
  /** The same name fully qualified, for providers that want the whole thing. */
  fqdn: string;
  value: string;
  purpose: "verify" | "apex" | "www" | "subdomain" | "vercel-verify";
}

/** Relative record name: `_polished-verify.acme.co.uk` under `acme.co.uk` → `_polished-verify`. */
export function dnsRecordName(fqdn: string, apex: string): string {
  const f = normaliseDomain(fqdn);
  if (f === apex) return "@";
  return f.endsWith(`.${apex}`) ? f.slice(0, -(apex.length + 1)) : f;
}

function stripDot(v: string): string {
  return String(v ?? "").trim().replace(/\.+$/, "");
}

/** The records an owner must add, in the order they should add them. */
export function buildDnsInstructions(opts: {
  domain: string;
  includeWww: boolean;
  token: string;
  ipv4?: string | null;
  cname?: string | null;
  vercelChallenges?: VercelChallenge[] | null;
}): DnsRecord[] {
  const domain = normaliseDomain(opts.domain);
  const apex = apexOf(domain);
  const ipv4 = stripDot(opts.ipv4 || DEFAULT_APEX_A);
  const cname = stripDot(opts.cname || DEFAULT_CNAME);
  const records: DnsRecord[] = [
    {
      key: "verify",
      type: "TXT",
      name: dnsRecordName(verificationHost(domain), apex),
      fqdn: verificationHost(domain),
      value: opts.token,
      purpose: "verify",
    },
  ];

  if (domain === apex) {
    records.push({ key: "apex", type: "A", name: "@", fqdn: domain, value: ipv4, purpose: "apex" });
    if (opts.includeWww) {
      records.push({ key: "www", type: "CNAME", name: "www", fqdn: `www.${domain}`, value: cname, purpose: "www" });
    }
  } else {
    records.push({
      key: "subdomain",
      type: "CNAME",
      name: dnsRecordName(domain, apex),
      fqdn: domain,
      value: cname,
      purpose: "subdomain",
    });
  }

  (opts.vercelChallenges ?? [])
    .filter((c) => String(c?.type).toUpperCase() === "TXT" && c.domain && c.value)
    .forEach((c, i) => {
      records.push({
        key: `vercel-${i}`,
        type: "TXT",
        name: dnsRecordName(c.domain, apex),
        fqdn: normaliseDomain(c.domain),
        value: c.value,
        purpose: "vercel-verify",
      });
    });

  return records;
}

// ---------------------------------------------------------------------------
// Ownership: DNS-over-HTTPS TXT lookup
// ---------------------------------------------------------------------------

/** Strip surrounding double-quotes and whitespace that DoH wraps TXT data in. */
function unquote(s: string): string {
  return String(s ?? "").trim().replace(/^"(.*)"$/, "$1").trim();
}

/** Does any TXT record equal the challenge token (quotes/space-insensitive)? */
export function txtRecordsContainToken(records: string[], token: string): boolean {
  const wanted = unquote(token);
  if (!wanted) return false;
  return records.some((r) => unquote(r) === wanted);
}

type DohAnswer = { type?: number | string; data?: string };
type DohResponse = { Answer?: DohAnswer[] };

/** Extract TXT (type 16) record strings from a DoH JSON response. */
function parseDohTxt(json: DohResponse): string[] {
  const answers = Array.isArray(json.Answer) ? json.Answer : [];
  return answers
    .filter((a) => Number(a.type) === 16)
    .map((a) => unquote(String(a.data ?? "")));
}

export interface DnsTxtResult {
  verified: boolean;
  records: string[];
  error?: string;
}

/**
 * Real DNS-over-HTTPS TXT lookup of `_polished-verify.<domain>`. Returns whether
 * the challenge token is present. Never throws: a network/DNS failure resolves
 * to `{ verified: false, error }` so the caller can record a clean failure.
 */
export async function checkDomainTxt(opts: {
  domain: string;
  token: string;
  fetchImpl?: typeof fetch;
}): Promise<DnsTxtResult> {
  const doFetch = opts.fetchImpl ?? fetch;
  const host = verificationHost(opts.domain);
  let lastError = "DNS lookup failed";

  for (const endpoint of DOH_ENDPOINTS) {
    try {
      const url = `${endpoint}?name=${encodeURIComponent(host)}&type=TXT`;
      const res = await doFetch(url, { headers: { accept: "application/dns-json" } });
      if (!res.ok) {
        lastError = `DNS query failed (${res.status})`;
        continue;
      }
      const json = (await res.json()) as DohResponse;
      const records = parseDohTxt(json);
      return { verified: txtRecordsContainToken(records, opts.token), records };
    } catch (e) {
      lastError = e instanceof Error ? e.message : String(e);
    }
  }
  return { verified: false, records: [], error: lastError };
}

// ---------------------------------------------------------------------------
// Vercel API
// ---------------------------------------------------------------------------

export interface VercelEnv {
  token?: string;
  projectId?: string;
  teamId?: string;
}

/** Pull Vercel credentials from an env bag (Deno.env.toObject() or process.env). */
export function readVercelEnv(env: Record<string, string | undefined>): VercelEnv {
  return {
    token: env.VERCEL_API_TOKEN || undefined,
    projectId: env.VERCEL_PROJECT_ID || undefined,
    teamId: env.VERCEL_TEAM_ID || undefined,
  };
}

export function isVercelConfigured(v: VercelEnv): boolean {
  return Boolean(v.token && v.projectId);
}

type VercelErrorBody = { error?: { code?: string; message?: string } };

interface VercelReply<T> {
  status: number;
  ok: boolean;
  body: T & VercelErrorBody;
}

function vercelUrl(path: string, v: VercelEnv, extra: Record<string, string> = {}): string {
  const qs = new URLSearchParams(extra);
  if (v.teamId) qs.set("teamId", v.teamId);
  const q = qs.toString();
  return `${VERCEL_API_BASE}${path}${q ? `?${q}` : ""}`;
}

async function vercelCall<T>(
  doFetch: typeof fetch,
  v: VercelEnv,
  method: string,
  path: string,
  opts: { body?: unknown; query?: Record<string, string> } = {},
): Promise<VercelReply<T>> {
  const res = await doFetch(vercelUrl(path, v, opts.query), {
    method,
    headers: { Authorization: `Bearer ${v.token}`, "Content-Type": "application/json" },
    ...(opts.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
  });
  const body = (await res.json().catch(() => ({}))) as T & VercelErrorBody;
  return { status: res.status, ok: res.ok, body };
}

const projectPath = (v: VercelEnv) => `/projects/${encodeURIComponent(v.projectId ?? "")}`;

type ProjectDomain = {
  name?: string;
  apexName?: string;
  projectId?: string;
  redirect?: string | null;
  verified?: boolean;
  verification?: VercelChallenge[];
};

type DomainConfig = {
  misconfigured?: boolean;
  configuredBy?: string | null;
  recommendedIPv4?: Array<{ rank: number; value: string[] | string }>;
  recommendedCNAME?: Array<{ rank: number; value: string }>;
};

export type AttachProblem = "in_use_elsewhere" | "vercel_auth" | "vercel_error";

export interface AttachResult {
  host: string;
  attached: boolean;
  verified: boolean;
  challenges: VercelChallenge[];
  problem?: AttachProblem;
  error?: string;
}

function authProblem(status: number): boolean {
  return status === 401 || status === 403;
}

const IN_USE_MESSAGE = (host: string) =>
  `${host} is already connected to a different website on our hosting provider. ` +
  `Remove it from that website first (or ask whoever set it up to), then press Check again.`;

const SIDE_ISSUE_MESSAGE =
  "We couldn't talk to our hosting provider just now. This is on our side, not yours — " +
  "we'll keep trying. If it hasn't cleared within a day, please get in touch.";

/**
 * Make sure `host` is on OUR Vercel project. Idempotent: an existing attachment
 * is detected with a GET first. A 409 / `domain_already_in_use` is only treated
 * as success if a follow-up GET proves the domain is on our project — otherwise
 * it is attached to someone else's project and we report that clearly.
 */
export async function attachDomainToProject(opts: {
  host: string;
  redirectTo?: string | null;
  vercel: VercelEnv;
  fetchImpl?: typeof fetch;
}): Promise<AttachResult> {
  const doFetch = opts.fetchImpl ?? fetch;
  const v = opts.vercel;
  const host = normaliseDomain(opts.host);
  const base: AttachResult = { host, attached: false, verified: false, challenges: [] };
  const getPath = `/v9${projectPath(v)}/domains/${encodeURIComponent(host)}`;

  try {
    let current = await vercelCall<ProjectDomain>(doFetch, v, "GET", getPath);

    if (current.status === 404) {
      const add = await vercelCall<ProjectDomain>(doFetch, v, "POST", `/v10${projectPath(v)}/domains`, {
        body: {
          name: host,
          ...(opts.redirectTo ? { redirect: normaliseDomain(opts.redirectTo), redirectStatusCode: 308 } : {}),
        },
      });

      if (add.ok) {
        current = add;
      } else if (authProblem(add.status)) {
        // 403 can also mean "You don't have access to the domain you are adding".
        const msg = add.body?.error?.message ?? "";
        if (/access to the domain|don't have access/i.test(msg)) {
          return { ...base, problem: "in_use_elsewhere", error: IN_USE_MESSAGE(host) };
        }
        return { ...base, problem: "vercel_auth", error: SIDE_ISSUE_MESSAGE };
      } else {
        const code = add.body?.error?.code;
        const maybeOurs =
          add.status === 409 || add.status === 400 ||
          code === "domain_already_in_use" || code === "domain_already_exists";
        if (maybeOurs) {
          // Only OK if it really is on OUR project (e.g. an earlier attempt raced).
          const recheck = await vercelCall<ProjectDomain>(doFetch, v, "GET", getPath);
          if (recheck.ok) {
            current = recheck;
          } else if (add.status === 409 || code === "domain_already_in_use") {
            return { ...base, problem: "in_use_elsewhere", error: IN_USE_MESSAGE(host) };
          } else {
            return { ...base, problem: "vercel_error", error: add.body?.error?.message ?? `Vercel API error (${add.status})` };
          }
        } else {
          return { ...base, problem: "vercel_error", error: add.body?.error?.message ?? `Vercel API error (${add.status})` };
        }
      }
    } else if (authProblem(current.status)) {
      return { ...base, problem: "vercel_auth", error: SIDE_ISSUE_MESSAGE };
    } else if (!current.ok) {
      return { ...base, problem: "vercel_error", error: current.body?.error?.message ?? `Vercel API error (${current.status})` };
    }

    let verified = Boolean(current.body?.verified);
    let challenges = Array.isArray(current.body?.verification) ? current.body.verification : [];

    if (!verified) {
      // Vercel wants its own TXT challenge (domain used by another Vercel account).
      // Ask it to re-check — succeeds once the owner has added the record.
      const ver = await vercelCall<ProjectDomain>(doFetch, v, "POST", `${getPath}/verify`);
      if (ver.ok && ver.body?.verified) {
        verified = true;
        challenges = [];
      }
    }

    return { host, attached: true, verified, challenges };
  } catch (e) {
    return { ...base, problem: "vercel_error", error: e instanceof Error ? e.message : String(e) };
  }
}

export interface DnsConfigResult {
  ok: boolean;
  misconfigured: boolean;
  configuredBy: string | null;
  ipv4: string | null;
  cname: string | null;
  error?: string;
}

function rankOne<T extends { rank: number }>(list: T[] | undefined): T | undefined {
  return [...(list ?? [])].sort((a, b) => Number(a.rank) - Number(b.rank))[0];
}

/**
 * Vercel's view of the host's DNS: whether it points at Vercel and can get a
 * certificate (`misconfigured: false`), plus the project-specific recommended
 * A / CNAME values (rank 1 is preferred). Works before the domain is attached
 * when `projectIdOrName` is passed.
 */
export async function getDomainDnsConfig(opts: {
  host: string;
  vercel: VercelEnv;
  fetchImpl?: typeof fetch;
}): Promise<DnsConfigResult> {
  const doFetch = opts.fetchImpl ?? fetch;
  const v = opts.vercel;
  const empty: DnsConfigResult = { ok: false, misconfigured: true, configuredBy: null, ipv4: null, cname: null };
  try {
    const res = await vercelCall<DomainConfig>(
      doFetch, v, "GET", `/v6/domains/${encodeURIComponent(normaliseDomain(opts.host))}/config`,
      { query: { projectIdOrName: v.projectId ?? "" } },
    );
    if (!res.ok) return { ...empty, error: res.body?.error?.message ?? `Vercel API error (${res.status})` };
    const ipv4Raw = rankOne(res.body.recommendedIPv4)?.value;
    const ipv4 = Array.isArray(ipv4Raw) ? ipv4Raw[0] ?? null : ipv4Raw ?? null;
    const cname = rankOne(res.body.recommendedCNAME)?.value ?? null;
    return {
      ok: true,
      misconfigured: res.body.misconfigured !== false,
      configuredBy: res.body.configuredBy ?? null,
      ipv4: ipv4 ? stripDot(ipv4) : null,
      cname: cname ? stripDot(cname) : null,
    };
  } catch (e) {
    return { ...empty, error: e instanceof Error ? e.message : String(e) };
  }
}

/** Detach a host from our project. A 404 (already gone) counts as success. */
export async function removeDomainFromProject(opts: {
  host: string;
  vercel: VercelEnv;
  fetchImpl?: typeof fetch;
}): Promise<{ ok: boolean; error?: string }> {
  const doFetch = opts.fetchImpl ?? fetch;
  const v = opts.vercel;
  const host = normaliseDomain(opts.host);
  if (isManagedSubdomain(host)) return { ok: false, error: "Refusing to detach the platform domain" };
  try {
    const res = await vercelCall<Record<string, unknown>>(
      doFetch, v, "DELETE", `/v9${projectPath(v)}/domains/${encodeURIComponent(host)}`,
    );
    if (res.ok || res.status === 404) return { ok: true };
    return { ok: false, error: res.body?.error?.message ?? `Vercel API error (${res.status})` };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}

/** Detach www (which redirects to the apex) first, then the primary host. */
export async function detachCustomDomain(opts: {
  domain: string;
  includeWww: boolean;
  vercel: VercelEnv;
  fetchImpl?: typeof fetch;
}): Promise<{ ok: boolean; skipped?: boolean; error?: string }> {
  if (!isVercelConfigured(opts.vercel)) return { ok: true, skipped: true };
  const hosts = customDomainHosts(normaliseDomain(opts.domain), opts.includeWww).reverse();
  const errors: string[] = [];
  for (const host of hosts) {
    const r = await removeDomainFromProject({ host, vercel: opts.vercel, fetchImpl: opts.fetchImpl });
    if (!r.ok && r.error) errors.push(`${host}: ${r.error}`);
  }
  return errors.length ? { ok: false, error: errors.join("; ") } : { ok: true };
}

// ---------------------------------------------------------------------------
// Whole-domain check
// ---------------------------------------------------------------------------

export type TlsStatus = "pending" | "provisioning" | "active" | "error" | "unconfigured";

/** What the owner sees. Derived from (verification_status, tls_status). */
export type DomainStage = "checking_dns" | "waiting_ssl" | "live" | "problem" | "setup_pending";

export function stageFor(verificationStatus: string, tlsStatus: string): DomainStage {
  if (tlsStatus === "error") return "problem";
  if (verificationStatus !== "verified") return "checking_dns";
  if (tlsStatus === "active") return "live";
  if (tlsStatus === "provisioning") return "waiting_ssl";
  if (tlsStatus === "unconfigured") return "setup_pending";
  return "checking_dns";
}

export interface HostState {
  name: string;
  attached: boolean;
  verified: boolean;
  misconfigured: boolean | null;
}

export interface DomainDnsState {
  ipv4: string | null;
  cname: string | null;
  txtFound: boolean;
  challenges: VercelChallenge[];
  hosts: HostState[];
  /** Short machine-readable reason for the current stage. */
  reason: string;
}

export interface DomainCheckResult {
  verified: boolean;
  tlsStatus: TlsStatus;
  stage: DomainStage;
  attached: boolean;
  problem: string | null;
  dns: DomainDnsState;
}

/** Real HTTPS request: succeeds only once a valid certificate is being served. */
export async function probeHttps(host: string, fetchImpl?: typeof fetch): Promise<boolean> {
  const doFetch = fetchImpl ?? fetch;
  try {
    const signal = typeof AbortSignal !== "undefined" && "timeout" in AbortSignal
      ? AbortSignal.timeout(8000)
      : undefined;
    await doFetch(`https://${host}/`, { method: "HEAD", redirect: "manual", signal });
    return true;
  } catch {
    return false;
  }
}

/**
 * Run every step for one stored domain and say where it has got to. Never
 * throws. The caller persists the result (record_custom_domain_check).
 */
export async function checkCustomDomain(opts: {
  domain: string;
  includeWww: boolean;
  token: string;
  alreadyVerified: boolean;
  vercel: VercelEnv;
  fetchImpl?: typeof fetch;
  probe?: (host: string) => Promise<boolean>;
}): Promise<DomainCheckResult> {
  const domain = normaliseDomain(opts.domain);
  const hosts = customDomainHosts(domain, opts.includeWww);
  const configured = isVercelConfigured(opts.vercel);
  const dns: DomainDnsState = {
    ipv4: null,
    cname: null,
    txtFound: opts.alreadyVerified,
    challenges: [],
    hosts: hosts.map((name) => ({ name, attached: false, verified: false, misconfigured: null })),
    reason: "",
  };

  const loadRecommended = async () => {
    if (!configured) return;
    // The primary host's config gives the A (apex) or CNAME (subdomain) value;
    // for an apex domain the www host's config gives the CNAME value.
    const configs = await Promise.all(
      hosts.map((host) => getDomainDnsConfig({ host, vercel: opts.vercel, fetchImpl: opts.fetchImpl })),
    );
    configs.forEach((c, i) => {
      if (!c.ok) return;
      dns.hosts[i].misconfigured = c.misconfigured;
      if (i === 0 && isApexDomain(domain)) dns.ipv4 = c.ipv4 ?? dns.ipv4;
      if (i === 0 && !isApexDomain(domain)) dns.cname = c.cname ?? dns.cname;
      if (i === 1) dns.cname = c.cname ?? dns.cname;
    });
    return configs;
  };

  // 1. Ownership.
  let verified = opts.alreadyVerified;
  if (!verified) {
    const txt = await checkDomainTxt({ domain, token: opts.token, fetchImpl: opts.fetchImpl });
    verified = txt.verified;
    dns.txtFound = txt.verified;
    if (!verified) {
      await loadRecommended();
      dns.reason = txt.error ? "dns_lookup_failed" : "txt_missing";
      return { verified: false, tlsStatus: "pending", stage: "checking_dns", attached: false, problem: null, dns };
    }
  }

  // 2. Attach (needs Vercel credentials).
  if (!configured) {
    dns.reason = "vercel_unconfigured";
    return {
      verified, tlsStatus: "unconfigured", stage: "setup_pending", attached: false,
      problem: "Automatic hosting setup is not configured yet (VERCEL_API_TOKEN / VERCEL_PROJECT_ID).",
      dns,
    };
  }

  const attaches: AttachResult[] = [];
  for (const [i, host] of hosts.entries()) {
    const r = await attachDomainToProject({
      host,
      redirectTo: i === 0 ? null : domain,
      vercel: opts.vercel,
      fetchImpl: opts.fetchImpl,
    });
    attaches.push(r);
    dns.hosts[i].attached = r.attached;
    dns.hosts[i].verified = r.verified;
    dns.challenges.push(...r.challenges);
    if (i === 0 && !r.attached) break; // www redirects to the primary, so it can't go first
  }

  const failed = attaches.find((a) => !a.attached);
  if (failed) {
    dns.reason = failed.problem ?? "vercel_error";
    return {
      verified,
      tlsStatus: "error",
      stage: "problem",
      attached: attaches[0]?.attached ?? false,
      problem: failed.error ?? SIDE_ISSUE_MESSAGE,
      dns,
    };
  }

  // 3. Vercel's own ownership challenge, if any.
  if (attaches.some((a) => !a.verified)) {
    await loadRecommended();
    dns.reason = "vercel_challenge";
    return { verified, tlsStatus: "pending", stage: "checking_dns", attached: true, problem: null, dns };
  }

  // 4. DNS pointing at Vercel?
  const configs = await loadRecommended();
  const primary = configs?.[0];
  if (!primary?.ok || primary.misconfigured) {
    dns.reason = primary?.ok ? "dns_not_pointing" : "config_unavailable";
    return { verified, tlsStatus: "pending", stage: "checking_dns", attached: true, problem: null, dns };
  }

  // 5. Certificate served?
  const probe = opts.probe ?? ((h: string) => probeHttps(h, opts.fetchImpl));
  const live = await probe(domain);
  dns.reason = live ? "live" : "waiting_ssl";
  return {
    verified,
    tlsStatus: live ? "active" : "provisioning",
    stage: live ? "live" : "waiting_ssl",
    attached: true,
    problem: null,
    dns,
  };
}
